import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AiAllowance, FormActivity, FormSummary, aiBuilderStatusApi, listForms,
} from '../../api/forms';
import { FolderSummary, Level } from '../../api/access';
import { adminMe } from '../../api/admin';
import { PlanLimitBody, planLimitOf, setFormDisabledApi } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { ActionBar, Modal, RowMenu } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import DuplicateFormDialog from './DuplicateFormDialog';
import DeleteFormModal from './DeleteFormDialog';
import FolderRail, { FolderChips, FolderSheet, RailSelection } from './FolderRail';
import ShareDialog from './ShareDialog';
import DistributeDialog from './DistributeDialog';
import NewFormModal, { NewFormMode } from './NewFormModal';
import { DeleteFolderModal, MoveFormsModal, NewFolderModal, RenameFolderModal } from './FolderDialogs';

/**
 * The forms list (R2, screens 06–07): a folder rail, and one row per form
 * that answers "is it live, is it busy, who can see it?" without opening
 * anything.
 *
 * What the redesign adds (D14, all from one list call): the status filter
 * with a derived Closed, the "Last 14 days" sparkline with its trend note,
 * "{n} new" from triage, and "{m} responses this month" in the header. What
 * it keeps from 044 and M3, in the design's language: the row menu (⋯), the
 * checkbox column and the bulk Move/Disable (now the ink ActionBar), the
 * Access cell folded under the Folder column, the Member's "Shared with me"
 * view, the phone cards, chips and Select mode, and every create path — now
 * one New form modal with three modes.
 *
 * Three things moved in 044 and each one bought space:
 *
 *   * the Public link column became the form's mono sub-line;
 *   * the five row buttons collapsed into one `⋯` (rule 5);
 *   * "Share…" means exactly one thing on this screen: who in your account can
 *     reach this. The public form's own access rules live on the Access tab.
 */

/** ONE editor at a time (rule 6). */
type Editor =
  | { kind: 'share'; subject: 'form' | 'folder'; id: number }
  | { kind: 'move'; forms: FormSummary[] }
  | { kind: 'newFolder' }
  | { kind: 'renameFolder'; folder: FolderSummary }
  | { kind: 'deleteFolder'; folder: FolderSummary }
  | { kind: 'delete'; form: FormSummary }
  | { kind: 'link'; form: FormSummary }
  | { kind: 'duplicate'; form: FormSummary }
  | null;

/** The header's segmented control. `closed` is derived on the server (§4). */
type StatusFilter = 'all' | 'live' | 'drafts' | 'closed';
const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'live', label: 'Live' }, { key: 'drafts', label: 'Drafts' }, { key: 'closed', label: 'Closed' },
];

/** Where a form sits in the filter: a draft, closed for a reason, or live. */
function statusOf(f: FormSummary): Exclude<StatusFilter, 'all'> {
  if (f.status === 'draft') return 'drafts';
  if (f.status === 'archived' || f.closed_reason) return 'closed';
  return 'live';
}

const NO_ACTIVITY: FormActivity = { recent: Array(14).fill(0), thisMonth: 0, new: 0 };

/**
 * "today", "yesterday", "3 days ago", "2 weeks ago", "4 months ago", then the
 * date — the Last response column (screen 06). The full timestamp rides on
 * the cell's title, so nothing is lost to the rounding.
 */
export function relativeDay(iso: string, now = Date.now()): string {
  const then = new Date(iso);
  const startOf = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const days = Math.round((startOf(new Date(now)) - startOf(then)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 28) { const w = Math.floor(days / 7); return `${w} ${w === 1 ? 'week' : 'weeks'} ago`; }
  if (days < 365) { const m = Math.max(1, Math.floor(days / 30)); return `${m} ${m === 1 ? 'month' : 'months'} ago`; }
  return then.toLocaleDateString();
}

/**
 * The one word under the folder name (screen 06: "Everyone", "Field ops",
 * "Private"): who can reach the form, in the fewest words. The full
 * sentence — who, how many, by what route — is the line's title, and the
 * Share dialog still lists every person and group.
 */
function accessWord(sharing: FormSummary['sharing'], isMember: boolean): string {
  if (!sharing) return '—';
  if (isMember) {
    const level = sharing.myLevel;
    return `You have ${level ? level[0].toUpperCase() + level.slice(1) : 'access'}`;
  }
  const { people, base } = sharing;
  if (base === 'everyone' && !people.length) return 'Everyone';
  if (base === 'restricted' && people.length === 1 && people[0].type === 'user') return 'Private';
  const groups = people.filter((p) => p.type === 'group').map((p) => p.name);
  const users = people.filter((p) => p.type === 'user').length;
  const parts = [...groups];
  if (users) parts.push(`${users} ${users === 1 ? 'person' : 'people'}`);
  if (base === 'everyone') parts.unshift('Everyone');
  return parts.length ? parts.join(' + ') : 'Only Admins';
}

export default function FormsList() {
  const [forms, setForms] = useState<FormSummary[] | null>(null);
  const [folders, setFolders] = useState<FolderSummary[]>([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [monthTotal, setMonthTotal] = useState<number | null>(null);
  const [ai, setAi] = useState<AiAllowance | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [accountSlug, setAccountSlug] = useState('');
  const [publicBaseUrl, setPublicBaseUrl] = useState<string | null>(null);
  const [platformBaseUrl, setPlatformBaseUrl] = useState<string | null>(null);
  const [me, setMe] = useState<{ role: string; isPlatformAdmin: boolean; admins: string[] } | null>(null);
  const [createLimit, setCreateLimit] = useState<PlanLimitBody | null>(null);
  const [reenableLimit, setReenableLimit] = useState<PlanLimitBody | null>(null);
  /** The New form modal, in one of its three modes; null while closed. */
  const [newForm, setNewForm] = useState<NewFormMode | null>(null);
  /** "Describe it": shown only when the platform console has a key saved. */
  const [aiAvailable, setAiAvailable] = useState(false);
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  /** Survives a move, so the bulk bar does not empty itself under the cursor. */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  /*
    D2 — the phone list. Under 600px the table is a card per form, the rail is
    a chip row, and selecting rows is a mode rather than a checkbox column that
    is always there. The desktop renders none of it: `phone` is false wherever
    the query does not match, including jsdom.
  */
  const phone = useMediaQuery(PHONE);
  const [managingFolders, setManagingFolders] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const navigate = useNavigate();
  // The rail selection and the status filter live in the URL so a view is
  // linkable and survives a refresh — the same treatment the saved views and
  // the submission drawer already get.
  const [params, setParams] = useSearchParams();

  const load = () => listForms()
    .then((r) => {
      setForms(r.forms);
      // Tolerant of a response without them: a rail with no folders is a
      // real state (3C), and it must not be a blank page.
      setFolders(r.folders || []);
      setUnfiledCount(r.unfiledCount ?? r.forms.filter((f) => !f.folder_id).length);
      setMonthTotal(r.responsesThisMonth ?? null);
      setAi(r.ai);
    })
    .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    aiBuilderStatusApi().then((r) => setAiAvailable(!!r.available)).catch(() => undefined);
    adminMe()
      .then((r) => {
        setAccountSlug(r.account?.slug || '');
        setPublicBaseUrl(r.formLinkBaseUrl || r.publicBaseUrl || null);
        setPlatformBaseUrl(r.publicBaseUrl || null);
        setMe({ role: r.admin.role, isPlatformAdmin: r.isPlatformAdmin, admins: r.admins || [] });
      })
      .catch(() => undefined);
  }, []);

  const isMember = !!me && me.role === 'member' && !me.isPlatformAdmin;
  const runsWorkspace = !!me && (me.role === 'owner' || me.role === 'admin' || me.isPlatformAdmin);

  const raw = params.get('folder');
  const selection: RailSelection = raw === 'unfiled' ? 'unfiled'
    : raw === 'shared' ? 'shared'
      : raw && Number.isFinite(Number(raw)) ? Number(raw)
        : 'all';
  const rawStatus = params.get('status');
  const status: StatusFilter = rawStatus === 'live' || rawStatus === 'drafts' || rawStatus === 'closed' ? rawStatus : 'all';

  function select(next: RailSelection) {
    setSelected(new Set());
    const p = new URLSearchParams(params);
    if (next === 'all') p.delete('folder');
    else p.set('folder', String(next));
    setParams(p, { replace: true });
  }
  function setStatus(next: StatusFilter) {
    const p = new URLSearchParams(params);
    if (next === 'all') p.delete('status');
    else p.set('status', next);
    setParams(p, { replace: true });
  }

  const currentFolder = typeof selection === 'number'
    ? folders.find((f) => f.id === selection) || null
    : null;

  /** Public links are namespaced by account so two customers can share a slug. */
  const publicPath = (slug: string) => (accountSlug ? `/f/${accountSlug}/${slug}` : `/f/${slug}`);

  // Search and the status filter apply to the CURRENT rail selection, and the
  // placeholder and the card footer both say so.
  const inSelection = useMemo(() => (forms || []).filter((f) => {
    if (selection === 'unfiled') return !f.folder_id;
    if (typeof selection === 'number') return f.folder_id === selection;
    return true; // 'all' and 'shared' are the same set for a Member
  }), [forms, selection]);

  const statusCounts = useMemo(() => {
    const c = { all: inSelection.length, live: 0, drafts: 0, closed: 0 };
    for (const f of inSelection) c[statusOf(f)] += 1;
    return c;
  }, [inSelection]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inSelection.filter((f) =>
      (status === 'all' || statusOf(f) === status) && (!q || f.name.toLowerCase().includes(q)));
  }, [inSelection, status, search]);

  const selectedForms = (forms || []).filter((f) => selected.has(f.id));

  /**
   * Disabling is instant, reversible and never asks for confirmation — it is
   * the affordance that frees a form slot without deleting anything. The gated
   * direction is re-enabling: past the ceiling the server refuses with the
   * standard limit body, shown inline above the list.
   */
  async function onToggleDisabled(f: FormSummary) {
    setError('');
    setReenableLimit(null);
    try {
      await setFormDisabledApi(f.id, !f.disabled_at);
      load();
    } catch (err: any) {
      const limit = planLimitOf(err);
      if (limit) setReenableLimit(limit);
      else setError(err.message);
    }
  }

  async function bulkDisable() {
    setError('');
    for (const f of selectedForms) {
      if (f.disabled_at) continue;
      try { await setFormDisabledApi(f.id, true); } catch { /* the list reload tells the truth */ }
    }
    setSelected(new Set());
    load();
  }

  if (!forms) return <div className="fs-card pad">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;

  const totalReachable = forms.length;
  // "{m} responses this month": the server's figure for the whole reachable
  // set, or the rail selection's own sum, which is the same arithmetic.
  const monthCount = selection === 'all' || selection === 'shared'
    ? (monthTotal ?? forms.reduce((n, f) => n + (f.activity?.thisMonth ?? 0), 0))
    : inSelection.reduce((n, f) => n + (f.activity?.thisMonth ?? 0), 0);
  const monthLine = `${monthCount} ${monthCount === 1 ? 'response' : 'responses'} this month`;
  const formsWord = (n: number) => `${n} ${n === 1 ? 'form' : 'forms'}`;

  // The header's subline: counts, and for a Member the reason they see what
  // they see. It never claims a ceiling it cannot know.
  const subline = isMember
    ? (totalReachable
      ? `${formsWord(totalReachable)} shared with you · ${monthLine} · you are a Member — an Admin decides what you can reach`
      : `You are a Member of this account`)
    : currentFolder
      ? `${formsWord(currentFolder.form_count)} · ${monthLine} · ${
        currentFolder.base_access === 'everyone'
          ? `everyone in the account can ${currentFolder.base_level}`
          : 'restricted to the people it is shared with'}`
      : selection === 'unfiled'
        ? `${formsWord(inSelection.length)} · ${monthLine} · out of every folder`
        : `${formsWord(totalReachable)} · ${monthLine} · ${folders.length} ${
          folders.length === 1 ? 'folder' : 'folders'}${folders.length ? '' : ' yet'}`;

  const title = currentFolder ? currentFolder.name
    : selection === 'unfiled' ? 'Unfiled'
      : selection === 'shared' ? 'Shared with me'
        : 'All forms';

  const pick = (id: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id); else next.delete(id);
    setSelected(next);
  };

  const rowActions = (f: FormSummary) => ({
    onShare: () => setEditor({ kind: 'share', subject: 'form', id: f.id }),
    onMove: () => setEditor({ kind: 'move', forms: [f] }),
    onLink: () => setEditor({ kind: 'link', form: f }),
    onDuplicate: () => setEditor({ kind: 'duplicate', form: f }),
    onToggleDisabled: () => onToggleDisabled(f),
    onDelete: () => setEditor({ kind: 'delete', form: f }),
  });

  return (
    <div>
      {createLimit && (
        <div className="lim-modal" role="dialog" aria-modal="true" aria-label="Plan limit reached">
          <div className="lim-modal-card">
            <PlanLimitBlock body={createLimit} variant="modal">
              <button className="lim-btn-neutral" onClick={() => setCreateLimit(null)}>
                Manage my {createLimit.allowed} forms
              </button>
            </PlanLimitBlock>
          </div>
        </div>
      )}

      {/* Rail + content, edge to edge (playbook §1a, screen 06): the folder
          rail is the page's own 220px left column on a desktop and the head,
          the tools and the list are the padded page beside it. On a phone
          the rail is the chip row under the head, as F1 built it. */}
      <div className="fl-page">
      {!phone && (
        <aside className="fl-side" aria-label="Folders">
        <FolderRail
          folders={folders}
          total={totalReachable}
          unfiled={unfiledCount}
          selection={selection}
          onSelect={select}
          isMember={isMember}
          canManageFolders={runsWorkspace}
          onNewFolder={() => setEditor({ kind: 'newFolder' })}
          onRenameFolder={(f) => setEditor({ kind: 'renameFolder', folder: f })}
          onShareFolder={(f) => setEditor({ kind: 'share', subject: 'folder', id: f.id })}
          onDeleteFolder={(f) => setEditor({ kind: 'deleteFolder', folder: f })}
        />
        </aside>
      )}
      <div className="fl-main">
      <div className="fs-page-head fl-head">
        <div className="text">
          <h1>{title}</h1>
          <div className="fs-page-sub">
            {subline}
            {currentFolder && runsWorkspace && (
              <>
                {' · '}
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setEditor({ kind: 'share', subject: 'folder', id: currentFolder.id });
                }}>Share this folder →</a>
              </>
            )}
          </div>
        </div>
        {!!forms.length && (
          <div className="fl-head-tools">
            <input
              className="fs-input fl-filter"
              aria-label="Filter forms"
              placeholder={currentFolder ? `Filter ${currentFolder.name}…` : 'Filter forms…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="fs-seg" role="group" aria-label="Status">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.key} type="button" className={status === s.key ? 'on' : ''} aria-pressed={status === s.key}
                  onClick={() => setStatus(s.key)}
                >
                  {s.label} <span className="count">{statusCounts[s.key]}</span>
                </button>
              ))}
            </div>
            {phone && (
              <button
                type="button" className={`fs-btn neutral${selecting ? ' on' : ''}`}
                aria-pressed={selecting}
                onClick={() => { setSelecting((v) => !v); if (selecting) setSelected(new Set()); }}
              >
                {selecting ? 'Done' : 'Select'}
              </button>
            )}
            <button className="fs-btn primary fl-new" type="button" onClick={() => setNewForm('blank')}>
              + New form
            </button>
          </div>
        )}
      </div>

      {newForm && (
        <NewFormModal
          initialMode={newForm}
          folders={folders}
          initialFolderId={currentFolder?.id ?? null}
          aiAvailable={aiAvailable}
          ai={ai}
          onClose={() => setNewForm(null)}
          onCreated={(form) => navigate(`/admin/forms/${form.id}/build`)}
          onLimit={setCreateLimit}
        />
      )}

      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}
      {reenableLimit && <div style={{ marginBottom: 14 }}><PlanLimitBlock body={reenableLimit} /></div>}

      {phone && (
        <FolderChips
          folders={folders}
          total={totalReachable}
          unfiled={unfiledCount}
          selection={selection}
          onSelect={select}
          isMember={isMember}
          canManageFolders={runsWorkspace}
          onNewFolder={() => setEditor({ kind: 'newFolder' })}
          onManage={() => setManagingFolders(true)}
        />
      )}

        <div className="fl-list">
          {!forms.length ? (
            <EmptyState isMember={isMember} admins={me?.admins || []} onNew={() => setNewForm('blank')} onTemplates={() => setNewForm('template')} />
          ) : phone ? (
            <div className="fl-cards">
              {visible.map((f) => (
                <FormCard
                  key={f.id}
                  form={f} isMember={isMember} path={publicPath(f.slug)}
                  selecting={selecting} picked={selected.has(f.id)}
                  onPick={(on) => pick(f.id, on)}
                  menuOpen={openMenuId === f.id}
                  onMenu={(o) => setOpenMenuId(o ? f.id : null)}
                  {...rowActions(f)}
                />
              ))}
              {!visible.length && (
                <div className="fs-card pad fl-empty">
                  No forms match. <button type="button" className="fs-btn text" onClick={() => { setSearch(''); setStatus('all'); }}>Clear filters</button>
                </div>
              )}
              <div className="fs-cell-sub fl-cards-foot">
                Showing {visible.length} of {totalReachable}
                {' · '}
                {currentFolder
                  ? `Search and filter apply to ${currentFolder.name}.`
                  : 'Search and filter apply to All forms. Pick a folder to narrow them.'}
              </div>
            </div>
          ) : (
            <div className="fs-card" style={{ padding: 0 }}>
              {/* F6 tier two: between 601 and 900 the columns are wider than
                  the card, and M0's `overflow-x: clip` on the shell means the
                  last ones would simply be unreachable. The wrapper lets them
                  scroll inside the card, first column pinned. Above 900 the
                  table fits and the wrapper does nothing. */}
              <div className="fs-table-scroll">
                <table className="fs-table fl-table">
                  <thead>
                    <tr>
                      <th className="fl-th-pick" />
                      <th>Form</th>
                      <th>Folder</th>
                      <th>Status</th>
                      <th className="fl-th-spark">Last 14 days</th>
                      <th>Responses</th>
                      <th>Last response</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((f) => (
                      <tr
                        key={f.id}
                        className="clickable"
                        // The design opens the form from anywhere on the row;
                        // the row's own controls keep their own jobs.
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest('a, button, input, label, .fs-menu-anchor')) return;
                          navigate(`/admin/forms/${f.id}/build`);
                        }}
                      >
                        <td className="fl-td-pick">
                          <input
                            type="checkbox"
                            aria-label={`Select ${f.name}`}
                            checked={selected.has(f.id)}
                            onChange={(e) => pick(f.id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <Link to={`/admin/forms/${f.id}/build`} className="fl-name">{f.name}</Link>
                          {f.has_draft && f.status !== 'draft' && (
                            <span className="fs-pill warn fl-unpublished" title="Saved draft — not published yet">unpublished changes</span>
                          )}
                          <div className="fl-url">
                            {f.status === 'published'
                              ? <a href={publicPath(f.slug)} target="_blank" rel="noreferrer">{publicPath(f.slug)} ↗</a>
                              : publicPath(f.slug)}
                          </div>
                        </td>
                        <td>
                          <div className="fl-folder">{f.folder_name || <span className="faint">Unfiled</span>}</div>
                          <div className="fl-access" title={f.sharing ? describe(f.sharing) : undefined}>{accessWord(f.sharing, isMember)}</div>
                        </td>
                        <td><StatusCell form={f} /></td>
                        <td><ActivityCell form={f} /></td>
                        <td>
                          <div className="fl-resp">
                            <Link to={`/admin/forms/${f.id}/submissions`}>{f.submission_count.toLocaleString()}</Link>
                            <NewBadge form={f} />
                          </div>
                        </td>
                        <td className="fl-last">
                          {f.last_submission_at
                            ? <span title={new Date(f.last_submission_at).toLocaleString()}>{relativeDay(f.last_submission_at)}</span>
                            : <span className="faint">—</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <RowMenu open={openMenuId === f.id} onToggle={(o) => setOpenMenuId(o ? f.id : null)}>
                            <RowActions form={f} {...rowActions(f)} />
                          </RowMenu>
                        </td>
                      </tr>
                    ))}
                    {!visible.length && (
                      <tr className="fl-empty-row">
                        <td colSpan={8}>
                          No forms match. <button type="button" className="fs-btn text" onClick={() => { setSearch(''); setStatus('all'); }}>Clear filters</button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="fs-card-foot">
                <span>
                  Showing {visible.length} of {totalReachable}
                  {' · '}
                  {currentFolder
                    ? `Search and filter apply to ${currentFolder.name}.`
                    : 'Search and filter apply to All forms. Pick a folder to narrow them.'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* The bulk actions, as the design's ink bar (from Responses): the same
          three calls the card footer carried, on a desktop as soon as a row is
          checked and on a phone in Select mode (F11). */}
      {selected.size > 0 && (!phone || selecting) && (
        <ActionBar className="ink fl-bulk">
          <b>{selected.size} selected</b>
          <span style={{ flex: 1 }} />
          <button className="fs-btn neutral sm" onClick={() => setEditor({ kind: 'move', forms: selectedForms })}>
            Move to…
          </button>
          <button className="fs-btn neutral sm" onClick={bulkDisable}>Disable</button>
          <button className="fs-btn neutral sm" onClick={() => setSelected(new Set())}>Clear</button>
        </ActionBar>
      )}

      {managingFolders && (
        <FolderSheet
          folders={folders}
          onClose={() => setManagingFolders(false)}
          onNewFolder={() => setEditor({ kind: 'newFolder' })}
          onRenameFolder={(f) => setEditor({ kind: 'renameFolder', folder: f })}
          onShareFolder={(f) => setEditor({ kind: 'share', subject: 'folder', id: f.id })}
          onDeleteFolder={(f) => setEditor({ kind: 'deleteFolder', folder: f })}
        />
      )}

      {/* ---- one editor at a time (rule 6) ---- */}
      {editor?.kind === 'share' && (
        <ShareDialog
          kind={editor.subject}
          id={editor.id}
          onClose={() => setEditor(null)}
          onSaved={load}
        />
      )}
      {editor?.kind === 'move' && (
        <MoveFormsModal
          forms={editor.forms}
          folders={folders}
          onClose={() => setEditor(null)}
          onDone={() => { setSelected(new Set()); load(); }}
        />
      )}
      {editor?.kind === 'newFolder' && (
        <NewFolderModal onClose={() => setEditor(null)} onDone={() => load()} />
      )}
      {editor?.kind === 'renameFolder' && (
        <RenameFolderModal folder={editor.folder} onClose={() => setEditor(null)} onDone={load} />
      )}
      {editor?.kind === 'deleteFolder' && (
        <DeleteFolderModal
          folder={editor.folder}
          onClose={() => setEditor(null)}
          onDone={() => { select('all'); load(); }}
        />
      )}
      {editor?.kind === 'delete' && (
        <DeleteFormModal
          form={editor.form}
          onClose={() => setEditor(null)}
          onDone={() => { setEditor(null); load(); }}
          onError={setError}
        />
      )}
      {editor?.kind === 'link' && (
        <DistributeDialog
          form={{ id: editor.form.id, name: editor.form.name, slug: editor.form.slug }}
          accountSlug={accountSlug} publicBaseUrl={publicBaseUrl} platformBaseUrl={platformBaseUrl}
          accessMode={editor.form.access?.mode} onClose={() => setEditor(null)}
        />
      )}
      {editor?.kind === 'duplicate' && (
        <DuplicateFormDialog
          form={editor.form}
          onClose={() => setEditor(null)}
          onDone={(copy) => { setEditor(null); navigate(`/admin/forms/${copy.id}/build`); }}
        />
      )}
    </div>
  );
}

/** The status pill — Live / Draft / Closed — and the one line that says why it is closed. */
function StatusCell({ form: f }: { form: FormSummary }) {
  const kind = statusOf(f);
  const label = kind === 'live' ? 'Live' : kind === 'drafts' ? 'Draft' : f.status === 'archived' ? 'Archived' : 'Closed';
  const tone = kind === 'live' ? 'ok' : kind === 'drafts' ? 'neutral' : 'warn';
  return (
    <>
      <span className={`fs-pill ${tone}`}>{label}</span>
      {f.disabled_at && (
        <div className="fs-cell-sub warn" title="Not collecting — submissions are kept, and it no longer counts against the form limit">
          not collecting · slot freed
        </div>
      )}
      {!f.disabled_at && f.closed_reason === 'closed_schedule' && <div className="fs-cell-sub" title="Past the closing date in its schedule">by its schedule</div>}
      {!f.disabled_at && f.closed_reason === 'closed_quota' && <div className="fs-cell-sub" title="Reached the response limit in its schedule">limit reached</div>}
    </>
  );
}

/**
 * The "Last 14 days" column: fourteen bars and a trend note that compares
 * this week with the one before — "↑3 wk", "↓1 wk", "quiet", "not live" or
 * "closed" (README §All forms).
 */
function ActivityCell({ form: f }: { form: FormSummary }) {
  const a = f.activity ?? NO_ACTIVITY;
  const total = a.recent.reduce((x, y) => x + y, 0);
  const before = a.recent.slice(0, 7).reduce((x, y) => x + y, 0);
  const week = a.recent.slice(7).reduce((x, y) => x + y, 0);
  const kind = statusOf(f);
  const note = kind === 'drafts' ? { text: 'not live', tone: '' }
    : kind === 'closed' ? { text: 'closed', tone: '' }
      : total === 0 ? { text: 'quiet', tone: '' }
        : { text: `${week >= before ? '↑' : '↓'}${week} wk`, tone: week > before ? ' up' : week < before ? ' down' : '' };
  return (
    <div className="fl-spark-cell">
      <Sparkline recent={a.recent} />
      <span className={`fl-trend${note.tone}`} title={`${week} in the last 7 days · ${before} in the 7 before`}>{note.text}</span>
    </div>
  );
}

/** Fourteen 5px bars, oldest on the left; grey when nothing came in. */
function Sparkline({ recent }: { recent: number[] }) {
  const total = recent.reduce((x, y) => x + y, 0);
  const max = Math.max(1, ...recent);
  return (
    <span className={`fl-spark${total ? '' : ' empty'}`} role="img" aria-label={`${total} ${total === 1 ? 'response' : 'responses'} in the last 14 days`}>
      {recent.map((n, i) => (
        <span key={i} className="fl-spark-bar" style={{ height: `${Math.max(8, Math.round((n / max) * 100))}%` }} data-n={n} />
      ))}
    </span>
  );
}

/** "{n} new": responses still in triage `new`, straight to the Responses tab. */
function NewBadge({ form: f }: { form: FormSummary }) {
  const n = f.activity?.new ?? 0;
  if (!n) return null;
  return <Link to={`/admin/forms/${f.id}/submissions`} className="fl-new-badge">{n} new</Link>;
}

/**
 * One form, as a card (D2, restyled in R2).
 *
 * The table's columns in four lines, in the order the questions are asked:
 * what is it and is it live, where does the public reach it, who can see it,
 * and how much has come in — now with the sparkline and "{n} new". The ⋯
 * carries the same `RowActions` the table row does, so a phone offers exactly
 * the actions a desktop does and no fewer — and on a coarse pointer M0 draws
 * it as an action sheet.
 */
function FormCard({
  form: f, isMember, path, selecting, picked, onPick, menuOpen, onMenu,
  onShare, onMove, onLink, onDuplicate, onToggleDisabled, onDelete,
}: {
  form: FormSummary;
  isMember: boolean;
  path: string;
  selecting: boolean;
  picked: boolean;
  onPick: (on: boolean) => void;
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  onShare: () => void;
  onMove: () => void;
  onLink: () => void;
  onDuplicate: () => void;
  onToggleDisabled: () => void;
  onDelete: () => void;
}) {
  const access = accessWord(f.sharing, isMember);
  const kind = statusOf(f);

  return (
    <div className={`fl-card${picked ? ' on' : ''}`}>
      {selecting && (
        <label className="fl-pick">
          <input
            type="checkbox" checked={picked} aria-label={`Select ${f.name}`}
            onChange={(e) => onPick(e.target.checked)}
          />
        </label>
      )}
      <div className="fl-card-body">
        <div className="fl-card-top">
          <Link to={`/admin/forms/${f.id}/build`} className="fl-card-name">{f.name}</Link>
          <NewBadge form={f} />
          {f.has_draft && f.status !== 'draft' && <span className="fs-pill warn" title="Saved draft — not published yet">unpublished changes</span>}
          <span className={`fs-pill ${kind === 'live' ? 'ok' : kind === 'drafts' ? 'neutral' : 'warn'}`}>
            {kind === 'live' ? 'Live' : kind === 'drafts' ? 'Draft' : f.status === 'archived' ? 'Archived' : 'Closed'}
          </span>
        </div>
        <div className="fl-url fl-card-path">
          {f.status === 'published'
            ? <a href={path} target="_blank" rel="noreferrer">{path} ↗</a>
            : path}
        </div>
        <div className="fs-cell-sub" title={f.sharing ? describe(f.sharing) : undefined}>{f.folder_name ? `${f.folder_name} · ` : ''}{access}</div>
        <div className="fs-cell-sub">
          <Link to={`/admin/forms/${f.id}/submissions`}>
            {f.submission_count.toLocaleString()} {f.submission_count === 1 ? 'response' : 'responses'}
          </Link>
          {' · '}
          {f.last_submission_at
            ? <span title={new Date(f.last_submission_at).toLocaleString()}>last {relativeDay(f.last_submission_at)}</span>
            : 'none yet'}
        </div>
        <div className="fl-card-spark"><ActivityCell form={f} /></div>
        {f.disabled_at && (
          <div className="fs-cell-sub warn">not collecting · slot freed</div>
        )}
        {!f.disabled_at && f.closed_reason === 'closed_schedule' && <div className="fs-cell-sub">closed by its schedule</div>}
        {!f.disabled_at && f.closed_reason === 'closed_quota' && <div className="fs-cell-sub">reached its response limit</div>}
      </div>
      <RowMenu open={menuOpen} onToggle={onMenu} label={f.name}>
        <RowActions
          form={f}
          onShare={onShare} onMove={onMove} onLink={onLink} onDuplicate={onDuplicate}
          onToggleDisabled={onToggleDisabled} onDelete={onDelete}
        />
      </RowMenu>
    </div>
  );
}

/**
 * The row menu, filtered by what this person may actually do.
 *
 * A View grant's menu is SHORT and carries one 12px line naming the level,
 * rather than five greyed items: an item you cannot use is worse than an item
 * that is not there, and the line answers the question the greying would raise.
 */
function RowActions({ form, onShare, onMove, onLink, onDuplicate, onToggleDisabled, onDelete }: {
  form: FormSummary;
  onShare: () => void;
  onMove: () => void;
  onLink: () => void;
  onDuplicate: () => void;
  onToggleDisabled: () => void;
  onDelete: () => void;
}) {
  const level: Level = form.sharing?.myLevel || 'view';
  const canEdit = level === 'edit' || level === 'manage';
  const canManage = level === 'manage';

  if (!canEdit) {
    return (
      <>
        <Link to={`/admin/forms/${form.id}/submissions`}>Open form</Link>
        <Link to={`/admin/forms/${form.id}/submissions`}>Responses</Link>
        <button onClick={onLink}>Get link…</button>
        <div className="rule" />
        <div className="fs-cell-sub" style={{ padding: '6px 12px' }}>
          You have <b>View</b>. Editing, sharing and deleting need Edit or Manage.
        </div>
      </>
    );
  }

  return (
    <>
      <Link to={`/admin/forms/${form.id}/build`}>Open in builder</Link>
      <Link to={`/admin/forms/${form.id}/submissions`}>Responses</Link>
      <div className="rule" />
      {canManage && <button onClick={onShare}>Share…</button>}
      {canManage && <button onClick={onMove}>Move to…</button>}
      <button onClick={onLink}>Get link…</button>
      <div className="rule" />
      <button onClick={onDuplicate}>Duplicate…</button>
      <button onClick={onToggleDisabled}>{form.disabled_at ? 'Re-enable' : 'Disable'}</button>
      {canManage && <>
        <div className="rule" />
        <button className="danger" onClick={onDelete}>Delete form…</button>
      </>}
    </>
  );
}

/**
 * The full access sentence — who, how many, and by what route — kept as the
 * title of the one-word access line (Ben, 2026-09-23: the list shows the
 * folder and one word, not the avatar stack).
 */
/** The sub-line: who, how many, and by what route. */
function describe(sharing: NonNullable<FormSummary['sharing']>): string {
  const { people, base, baseLevel } = sharing;
  if (base === 'everyone' && !people.length) return `everyone in the account can ${baseLevel}`;

  const groups = people.filter((p) => p.type === 'group');
  const users = people.filter((p) => p.type === 'user');
  const viaFolder = people.find((p) => p.viaFolder)?.viaFolder;

  const parts: string[] = [];
  for (const g of groups) parts.push(`${g.name} (${g.level})`);
  if (users.length) parts.push(`${users.length} ${users.length === 1 ? 'person' : 'people'}`);
  if (!parts.length) parts.push('only the Admins');

  const route = viaFolder ? ` · via ${viaFolder}` : ' · direct grants';
  return parts.join(' + ') + route;
}

/**
 * The two empties, drawn as different components because they are different
 * facts (3B and the ordinary no-forms-yet case).
 *
 * A Member with nothing shared is the single most common screen on rollout
 * morning. Plain, not alarming, no red, no "error" — and it names the way out
 * without pretending the Member can take it themselves.
 */
function EmptyState({ isMember, admins, onNew, onTemplates }: {
  isMember: boolean; admins: string[]; onNew: () => void; onTemplates: () => void;
}) {
  if (!isMember) {
    return (
      <div className="fs-card pad" style={{ textAlign: 'center' }}>
        <b style={{ fontSize: 'var(--text-card-title)' }}>No forms yet</b>
        <div className="fs-cell-sub" style={{ marginTop: 6 }}>
          Start blank and it opens straight in the builder — or start from something real.
        </div>
        <div className="fl-empty-actions">
          <button className="fs-btn primary" onClick={onNew}>+ New form</button>
          <button className="fs-btn neutral" onClick={onTemplates}>Browse the templates</button>
        </div>
      </div>
    );
  }
  return (
    <div className="fs-card pad" style={{ textAlign: 'center' }}>
      <b style={{ fontSize: 'var(--text-card-title)' }}>Nothing has been shared with you yet</b>
      <div className="fs-cell-sub" style={{ marginTop: 8, lineHeight: 1.6, maxWidth: 460, margin: '8px auto 0' }}>
        This account has forms — you just do not have access to any of them. An Owner
        or Admin can share a folder or a single form with you, and it will appear here.
      </div>
      <div className="fs-cell-sub faint" style={{ marginTop: 14 }}>
        Anything you make is private to you until you share it.
        {!!admins.length && <> Admins of this account: {admins.join(', ')}.</>}
      </div>
    </div>
  );
}


// The public link, QR code and embed snippets live in DistributeDialog (U6).
